import Link from "next/link";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

interface CtaProps {
  text: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

interface SectionProps {
  id?: string;
  title?: string;
  subtitle?: string | React.ReactNode;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  mainCta?: CtaProps;
  secondaryCta?: CtaProps;
  titleClassName?: string;
  subtitleClassName?: string;
  containerClassName?: string;
  addButtonGaps?: boolean;
  largerTopGap?: boolean;
  titleBlockClassName?: string;
  buttonStyles?: string;
}

export default function Section({
  id,
  title,
  subtitle,
  description,
  children,
  className,
  mainCta,
  secondaryCta,
  titleClassName,
  subtitleClassName,
  containerClassName = '',
  addButtonGaps,
  largerTopGap,
  titleBlockClassName,
  buttonStyles,
}: SectionProps) {
  const sectionId = title ? title.toLowerCase().replace(/\s+/g, "-") : id;
  return (
    <section id={id || sectionId} className="w-full">
      <div className={cn(className, "w-full h-full py-16 md:py-24")}>
        <div className={cn("container mx-auto px-6 max-w-6xl", containerClassName)}>
          {(title || subtitle || description) && (
            <div className={cn("text-center space-y-4 pb-10 lg:pb-12 mx-auto", titleBlockClassName)}>
              {title && (
                <h2 className={titleClassName || "text-sm tracking-wider text-primary font-mono font-medium tracking-wider uppercase"}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <h3 className={cn("mt-4 text-3xl font-semibold sm:max-w-none sm:text-4xl md:text-5xl break-words", subtitleClassName)}>
                  {subtitle}
                </h3>
              )}
              {description && (
                <p className="mt-6 text-lg leading-8 text-slate-600 max-w-2xl mx-auto">
                  {description}
                </p>
              )}
            </div>
          )}
          {children}
          
          {(mainCta || secondaryCta) && (
            <div className={cn("flex flex-col sm:flex-row items-center justify-center gap-4 md:mt-6 mt-8 lg:px-6", {
              "px-6": addButtonGaps === true,
              "mt-14": largerTopGap === true,
              
            },
            buttonStyles
            )}>
              {mainCta && (
                mainCta.href ? (
                  <Link
                    href={mainCta.href}
                    className={cn(
                      buttonVariants({ variant: "default" }),
                      "w-full sm:w-auto text-background flex gap-2"
                    )}
                  >
                    {mainCta.icon}
                    {mainCta.text}
                  </Link>
                ) : (
                  <button
                    onClick={mainCta.onClick}
                    className={cn(
                      buttonVariants({ variant: "default" }),
                      "w-full sm:w-auto text-background flex gap-2"
                    )}
                  >
                    {mainCta.icon}
                    {mainCta.text}
                  </button>
                )
              )}
              {secondaryCta && (
                secondaryCta.href ? (
                  <Link
                    href={secondaryCta.href}
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "w-full sm:w-auto"
                    )}
                  >
                    {secondaryCta.text}
                  </Link>
                ) : (
                  <button
                    onClick={secondaryCta.onClick}
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "w-full sm:w-auto"
                    )}
                  >
                    {secondaryCta.text}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
